/**
 * Deal stage-history SHAPES and per-deal arithmetic — no database, no
 * server-only imports, so a client card, a server route, a Slack block builder
 * and a Meridian tool all share ONE definition of "how long was this deal in
 * that stage".
 *
 * Rows come from `public.deal_stage_history` (migration
 * `20260906203420_deal_stage_history.sql`): an append-only log of transitions.
 * A row's stage lasted until the NEXT row's `changed_at`; the newest row's
 * stage is still running, so it lasted until `asOf`.
 *
 * Keep this module dependency-free. It is imported by client components; a
 * Supabase client or a `server-only` import here would drag the database into
 * the browser bundle (SERVER_CLIENT_IMPORT_BOUNDARY).
 */

/** Raw shape of a `public.deal_stage_history` row, as PostgREST returns it. */
export interface StageHistoryRow {
  id: string;
  from_stage: string | null;
  to_stage: string | null;
  changed_at: string;
  changed_by: string | null;
  is_backfill: boolean;
}

/** A history row carrying its deal, for cross-deal (aggregate) reads. */
export interface DealStageHistoryRow extends StageHistoryRow {
  deal_id: string;
}

/** One stage occupancy: the stage the deal entered, and how long it held it. */
export interface DealStageOccupancy {
  id: string;
  /** Stage the deal left. `null` on the deal's first entry. */
  fromStage: string | null;
  /** Stage the deal entered and then occupied for `durationMs`. */
  toStage: string | null;
  /** ISO timestamp the deal entered `toStage`. */
  changedAt: string;
  /** `auth.uid()` of the actor, when the write carried a user JWT. */
  changedBy: string | null;
  /**
   * `changedAt` is a seeded upper bound, not an observed transition — render
   * the duration as "at least N days", never as exact.
   */
  isApproximate: boolean;
  /** This is the stage the deal is in right now (the newest entry). */
  isCurrent: boolean;
  /** Milliseconds spent in `toStage`: to the next entry, or to `asOf`. */
  durationMs: number;
}

export interface DealStageHistory {
  /** Newest entry first. Empty when the deal has no history rows. */
  entries: DealStageOccupancy[];
  /** The newest entry, i.e. the stage the deal occupies now. */
  current: DealStageOccupancy | null;
  /** The instant durations were measured against (so a UI can re-render). */
  asOf: string;
}

/** Runtime shape check — the wire is untrusted (untyped client, or a fetch). */
export function isStageHistoryRow(value: unknown): value is StageHistoryRow {
  if (typeof value !== "object" || value === null) return false;
  // `in` narrowing rather than a cast: each access below is typed `unknown`,
  // so every field still has to be checked before it can be used.
  if (!("id" in value) || typeof value.id !== "string") return false;
  if (!("changed_at" in value) || typeof value.changed_at !== "string") return false;
  if (!("is_backfill" in value) || typeof value.is_backfill !== "boolean") return false;
  if (!("from_stage" in value) || !isNullableString(value.from_stage)) return false;
  if (!("to_stage" in value) || !isNullableString(value.to_stage)) return false;
  if (!("changed_by" in value) || !isNullableString(value.changed_by)) return false;
  return true;
}

/** Same check, plus the `deal_id` an aggregate read needs in order to group. */
export function isDealStageHistoryRow(value: unknown): value is DealStageHistoryRow {
  if (!isStageHistoryRow(value)) return false;
  return "deal_id" in value && typeof (value as { deal_id: unknown }).deal_id === "string";
}

function isNullableString(value: unknown): value is string | null {
  return value === null || typeof value === "string";
}

/**
 * Turn transition rows (NEWEST FIRST) into occupancies with durations.
 *
 * A row's stage ran until the NEXT transition — which, in a newest-first list,
 * is the row ABOVE it. The newest row has no successor, so its stage is still
 * running and it is measured against `asOf`.
 */
export function toStageOccupancies(
  rowsNewestFirst: readonly StageHistoryRow[],
  asOf: Date
): DealStageOccupancy[] {
  return rowsNewestFirst.map((row, index) => {
    const enteredAt = new Date(row.changed_at).getTime();
    const previous = rowsNewestFirst[index - 1];
    const leftAt = index === 0 ? asOf.getTime() : new Date(previous.changed_at).getTime();
    const durationMs =
      Number.isFinite(enteredAt) && Number.isFinite(leftAt) ? leftAt - enteredAt : 0;

    return {
      id: row.id,
      fromStage: row.from_stage,
      toStage: row.to_stage,
      changedAt: row.changed_at,
      changedBy: row.changed_by,
      isApproximate: row.is_backfill,
      isCurrent: index === 0,
      // Clock skew or an out-of-order write must not produce a negative age.
      durationMs: Math.max(0, durationMs),
    };
  });
}

/** Runtime shape check for an occupancy arriving over the wire (the API). */
export function isDealStageOccupancy(value: unknown): value is DealStageOccupancy {
  if (typeof value !== "object" || value === null) return false;
  if (!("id" in value) || typeof value.id !== "string") return false;
  if (!("changedAt" in value) || typeof value.changedAt !== "string") return false;
  if (!("isApproximate" in value) || typeof value.isApproximate !== "boolean") return false;
  if (!("isCurrent" in value) || typeof value.isCurrent !== "boolean") return false;
  if (!("durationMs" in value) || typeof value.durationMs !== "number") return false;
  if (!("fromStage" in value) || !isNullableString(value.fromStage)) return false;
  if (!("toStage" in value) || !isNullableString(value.toStage)) return false;
  if (!("changedBy" in value) || !isNullableString(value.changedBy)) return false;
  return true;
}

/**
 * Re-measure durations against a NEW instant, without another round trip.
 *
 * The server computed them at request time; a panel left open drifts, and the
 * only entry that drifts is the current one — but recomputing all of them from
 * `changedAt` keeps one implementation of the rule instead of a special case.
 */
export function refreshOccupancyDurations(
  entriesNewestFirst: readonly DealStageOccupancy[],
  asOf: Date
): DealStageOccupancy[] {
  return entriesNewestFirst.map((entry, index) => {
    const enteredAt = new Date(entry.changedAt).getTime();
    const previous = entriesNewestFirst[index - 1];
    const leftAt = index === 0 ? asOf.getTime() : new Date(previous.changedAt).getTime();
    const durationMs =
      Number.isFinite(enteredAt) && Number.isFinite(leftAt) ? leftAt - enteredAt : entry.durationMs;

    return { ...entry, isCurrent: index === 0, durationMs: Math.max(0, durationMs) };
  });
}

/**
 * Group cross-deal rows into per-deal, NEWEST-FIRST lists.
 *
 * The aggregate read pulls one flat result set; every downstream calculation
 * needs it sliced per deal, because a stage's duration is defined by the next
 * row FOR THE SAME DEAL. Sorting here rather than trusting the caller's ORDER
 * BY means nobody can silently get wrong durations by forgetting one. Ties on
 * `changed_at` break on `id` descending, matching the per-deal loader's stable
 * ordering (OFFSET_PAGINATION_STABILITY).
 */
export function groupRowsByDeal(
  rows: readonly DealStageHistoryRow[]
): Map<string, StageHistoryRow[]> {
  const byDeal = new Map<string, StageHistoryRow[]>();
  for (const row of rows) {
    const existing = byDeal.get(row.deal_id);
    if (existing) existing.push(row);
    else byDeal.set(row.deal_id, [row]);
  }
  for (const list of byDeal.values()) {
    list.sort((a, b) => {
      const delta = Date.parse(b.changed_at) - Date.parse(a.changed_at);
      if (Number.isFinite(delta) && delta !== 0) return delta;
      return a.id < b.id ? 1 : a.id > b.id ? -1 : 0;
    });
  }
  return byDeal;
}
